import React, { useState, useEffect } from 'react';
import { TickOverlay } from './TickOverlay';
import { subjectBorderClass as subjectBorder } from '../../shared/constants/subjects';
import { api } from '../../shared/utils/api';

// ─── Depth pips ───────────────────────────────────────────────────────────────

function depthPipsData(pc) {
  if (pc == null) return null;
  if (pc <= 7)  return { pips: ['light','off','off','off'], label: 'Light' };
  if (pc <= 14) return { pips: ['med','med','off','off'],   label: 'Medium' };
  return         { pips: ['heavy','heavy','heavy','heavy'], label: 'Heavy' };
}

function DepthPips({ principleCount }) {
  const data = depthPipsData(principleCount);
  if (!data) return null;
  return (
    <>
      <span className="depth-pips">
        {data.pips.map((p, i) => <span key={i} className={`depth-pip depth-pip-${p}`} />)}
      </span>
      <span className="depth-text">{data.label}</span>
    </>
  );
}

// ─── Conf colour helpers ──────────────────────────────────────────────────────

function confDoneStyle(confFraction) {
  const pct = Math.round(confFraction * 100);
  if (pct >= 70) return { background: '#DCFCE7', color: '#166534' };
  if (pct >= 40) return { background: '#FEF3C7', color: '#92400E' };
  return { background: '#FEE2E2', color: '#991B1B' };
}

function revWashClass(topicConf) {
  if (topicConf == null) return 'day-card-rev-amber';
  return topicConf < 0.5 ? 'day-card-rev-red' : 'day-card-rev-amber';
}

function confClass(conf, prefix) {
  if (conf >= 0.7) return `${prefix}-green`;
  if (conf >= 0.4) return `${prefix}-amber`;
  return `${prefix}-red`;
}

// ─── Topic card — day view (v3) ───────────────────────────────────────────────

export default function DayCard({ entry, dateStr, allWeekDates, onComplete, onMove, isPast, toggles, onNavigateToTopic }) {
  const [completing, setCompleting] = useState(false);
  const [localEntry, setLocalEntry] = useState(entry);
  useEffect(() => setLocalEntry(entry), [entry]);

  const e = localEntry;
  const isCompleted = e.status === 'completed';
  const isMoved = e.status === 'moved';
  const isRevision = e.planner_type === 'revision';
  const borderCls = subjectBorder(e.subject_area);
  const dayIndex = allWeekDates.indexOf(dateStr);
  const canAct = !isPast && !isCompleted && !isMoved;
  const canNavigate = !!(onNavigateToTopic && e.topic_id);
  const topicConf = e.topic_confidence;
  const showL2 = toggles?.l2 !== false;
  const showDepth = toggles?.depth !== false;

  const handleConfirm = async conf => {
    const res = await api('/api/planner/complete', {
      method: 'POST',
      body: JSON.stringify({ schedule_entry_id: e.id, confidence: conf }),
    });
    if (res.ok) {
      setLocalEntry(prev => ({ ...prev, status: 'completed', confidence_on_complete: conf }));
      onComplete(e.id, conf, res.overall_progress);
    }
    setCompleting(false);
  };

  const clickProps = canNavigate
    ? { onClick: () => onNavigateToTopic(e.topic_id, e.module), style: { cursor: 'pointer' } }
    : {};

  const actionButtons = canAct ? (
    <>
      <button className="btn-tick" onClick={ev => { ev.stopPropagation(); setCompleting(true); }} title="Mark as done">
        <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
      </button>
      <div className="day-card-arrows">
        <button className="btn-arrow" disabled={dayIndex <= 0}
          onClick={ev => { ev.stopPropagation(); dayIndex > 0 && onMove(e.id, allWeekDates[dayIndex - 1]); }}>
          <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <button className="btn-arrow" disabled={dayIndex >= allWeekDates.length - 1}
          onClick={ev => { ev.stopPropagation(); dayIndex < allWeekDates.length - 1 && onMove(e.id, allWeekDates[dayIndex + 1]); }}>
          <svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
      </div>
    </>
  ) : null;

  // ── Completed ──
  if (isCompleted) {
    const confStyle = confDoneStyle(e.confidence_on_complete ?? 0);
    const confPct = Math.round((e.confidence_on_complete ?? 0) * 100);
    return (
      <div className={`day-card-done ${borderCls}`}>
        <div className="day-card-body" {...clickProps}>
          <div className="day-card-row1">
            <span className="day-card-name">{e.subtopic}</span>
            {e.confidence_on_complete != null && (
              <span className="day-card-done-conf" style={confStyle}>{confPct}%</span>
            )}
          </div>
          <div className="day-card-row2">
            <span className="day-card-subject">{e.subject_area}</span>
            {showL2 && e.general_topic && (
              <><span className="day-card-sep">·</span><span className="day-card-l2">{e.general_topic}</span></>
            )}
            {e.principle_count != null && (
              <><span className="day-card-sep">·</span><span className="day-card-princ">{e.principle_count} principles</span></>
            )}
          </div>
        </div>
        <div className="day-card-actions">
          <div className="btn-tick-done"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg></div>
        </div>
      </div>
    );
  }

  // ── Moved ──
  if (isMoved) {
    return (
      <div className={`day-card-moved ${borderCls}`}>
        <div className="day-card-body">
          <div className="day-card-row1">
            <span className="day-card-name">{e.subtopic}</span>
            <span className="day-card-moved-label">Moved →</span>
          </div>
          <div className="day-card-row2">
            <span className="day-card-subject">{e.subject_area}</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Revision (pending) ──
  if (isRevision) {
    return (
      <>
        <div className={`day-card-rev ${revWashClass(topicConf)} ${borderCls}`} {...clickProps}>
          <div className="day-card-rev-body">
            <div className="day-card-row1">
              <span className="day-card-name">{e.subtopic}</span>
              {topicConf != null && (
                <span className={`day-card-conf ${confClass(topicConf, 'day-card-conf')}`}>
                  {Math.round(topicConf * 100)}%
                </span>
              )}
            </div>
            <div className="day-card-row2">
              <span className="day-card-subject">{e.subject_area}</span>
              {showL2 && e.general_topic && (
                <><span className="day-card-sep">·</span><span className="day-card-l2">{e.general_topic}</span></>
              )}
              {e.principle_count != null && (
                <><span className="day-card-sep">·</span><span className="day-card-princ">{e.principle_count} principles</span></>
              )}
              {showDepth && <DepthPips principleCount={e.principle_count} />}
              <span style={{ marginLeft: 'auto' }}><span className="day-card-revise-pill">Revise</span></span>
            </div>
          </div>
          <div className="day-card-rev-actions">
            {!isPast && actionButtons}
          </div>
        </div>
        {completing && <TickOverlay entry={e} onConfirm={handleConfirm} onCancel={() => setCompleting(false)} />}
      </>
    );
  }

  // ── New (pending) ──
  return (
    <>
      <div className={`day-card ${borderCls}`} {...clickProps}>
        <div className="day-card-body">
          <div className="day-card-row1">
            <span className="day-card-name">{e.subtopic}</span>
            <span className="day-card-new">New</span>
          </div>
          <div className="day-card-row2">
            <span className="day-card-subject">{e.subject_area}</span>
            {showL2 && e.general_topic && (
              <><span className="day-card-sep">·</span><span className="day-card-l2">{e.general_topic}</span></>
            )}
            {e.principle_count != null && (
              <><span className="day-card-sep">·</span><span className="day-card-princ">{e.principle_count} principles</span></>
            )}
            {showDepth && <DepthPips principleCount={e.principle_count} />}
          </div>
        </div>
        <div className="day-card-actions">
          {!isPast && actionButtons}
        </div>
      </div>
      {completing && <TickOverlay entry={e} onConfirm={handleConfirm} onCancel={() => setCompleting(false)} />}
    </>
  );
}
